import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { X, ScrollText } from 'lucide-react';
import TermsContent, { TERMS_LAST_UPDATED } from '../components/TermsContent';

/**
 * Global Terms & Conditions modal.
 *
 * Any component can call `useTermsModal().openTerms()` to pop the same
 * modal — used from the sidebar footer, About page, and the sign-up
 * form's "I accept the terms" affordance. Keeps the T&C content OUT of
 * a new tab / same window so the reading experience stays in-app.
 *
 * The `/terms` deep link still exists (see App.js) for external / shared
 * URLs — the modal is a convenience layer on top of the standalone page.
 */
const TermsModalContext = createContext({
  openTerms: () => {},
  closeTerms: () => {},
  isTermsOpen: false,
});

export const useTermsModal = () => useContext(TermsModalContext);

export const TermsModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const openTerms = useCallback(() => setIsOpen(true), []);
  const closeTerms = useCallback(() => setIsOpen(false), []);

  // Close on Escape — every other modal in the app honours this so
  // keyboard users can dismiss without hunting for the X.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeTerms(); };
    window.addEventListener('keydown', onKey);
    // Prevent the underlying page from scrolling while the modal is
    // stacked on top — matches the AuthModal / ConfirmDialog behaviour.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeTerms]);

  return (
    <TermsModalContext.Provider value={{ openTerms, closeTerms, isTermsOpen: isOpen }}>
      {children}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm"
          data-testid="terms-modal-overlay"
          onClick={closeTerms}
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-modal-title"
        >
          <div
            className="relative w-full max-w-3xl max-h-[85vh] rounded-2xl bg-white dark:bg-zinc-900 shadow-2xl border border-zinc-200 dark:border-zinc-800 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="terms-modal"
          >
            <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-300 flex-shrink-0">
                <ScrollText size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h2
                  id="terms-modal-title"
                  className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-white leading-tight truncate"
                  data-testid="terms-modal-title"
                >
                  Terms &amp; Conditions
                </h2>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5" data-testid="terms-modal-last-updated">
                  Last updated: {TERMS_LAST_UPDATED}
                </p>
              </div>
              <button
                type="button"
                onClick={closeTerms}
                aria-label="Close Terms & Conditions"
                data-testid="terms-modal-close-btn"
                className="p-2 rounded-full text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto px-5 sm:px-6 py-5">
              <TermsContent />
              <div className="pt-4 mt-6 border-t border-zinc-200 dark:border-zinc-800 text-xs text-zinc-500 dark:text-zinc-400">
                © {new Date().getFullYear()} Pray Now Together. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      )}
    </TermsModalContext.Provider>
  );
};
